import React, { useState } from 'react';

const PORTAL_URL = 'https://portal.dbos.world';

const TIER_LABELS = {
  free: 'Free', trial: 'Trial', starter: 'Starter',
  basic: 'Basic', plus: 'Plus', pro: 'Pro', premium: 'Premium',
};

const TIER_COLORS = {
  free: '#888', trial: '#888', starter: '#cd7f32',
  basic: '#aaa', plus: '#d4af37', pro: '#60a5fa', premium: '#a78bfa',
};

function ReferralCard({ gido, quota, sub }) {
  const [copied, setCopied] = useState(false);

  if (!gido?.gido_id) return null;

  const referralCode = sub?.referral_code || `REF-${gido.gido_id.replace('GIDO_', '').replace('_', '')}`;
  const referralLink = `${PORTAL_URL}/?ref=${referralCode}`;
  const referralCount = sub?.referral_count || 0;
  const baseTier = sub?.base_billing_tier || 'free';
  const currentTier = sub?.current_access_tier || quota?.tier || 'trial';
  const isTrial = quota?.trial === true;
  const isPaying = !isTrial && baseTier !== 'free';

  // Trial: show only from day 10+
  if (isTrial) {
    const trialStarted = quota?.trial_started?.toDate
      ? quota.trial_started.toDate()
      : new Date(quota?.trial_started);
    const daysUsed = Math.floor((new Date() - trialStarted) / (1000 * 60 * 60 * 24));
    if (daysUsed < 9) return null;
  }

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Referral link display (shared by all states) ──
  const ReferralLinkBox = () => (
    <div className="referral-link-box">
      <div className="referral-code-row">
        <span className="referral-code-label">Your code:</span>
        <span className="referral-code">{referralCode}</span>
      </div>
      <div className="referral-copy-row">
        <span className="referral-url">{referralLink}</span>
        <button className="referral-copy-btn" onClick={copyLink}>
          {copied ? '✅ Copied!' : '📋 Copy link'}
        </button>
      </div>
    </div>
  );

  // ── FREE / TRIAL card ──
  if (!isPaying) {
    return (
      <div className="referral-card free-tier">
        {referralCount === 0 && (
          <>
            <div className="referral-card-title">🎁 Refer 3 colleagues → get Starter free</div>
            <div className="referral-card-body">
              No credit card. No catch. Three paying subscribers and your Starter plan is on us — permanently.
            </div>
            <div className="referral-unlock-grid">
              <div className="referral-unlock-item">📦 25 GB storage</div>
              <div className="referral-unlock-item">🏷️ 1,000 AI ops/mo</div>
              <div className="referral-unlock-item">✦ All 5 content packs</div>
              <div className="referral-unlock-item">✦ Auto-categorization</div>
            </div>
            <ReferralLinkBox />
          </>
        )}
        {referralCount >= 1 && referralCount < 3 && (
          <>
            <div className="referral-card-title">🎁 {referralCount} of 3 — almost there</div>
            <div className="referral-card-body">
              One colleague already joined — {3 - referralCount} more and your Starter plan is free, no card needed.
            </div>
            <div className="referral-progress-bar">
              <div className="referral-progress-fill" style={{ width: `${Math.round((referralCount / 3) * 100)}%` }} />
            </div>
            <div className="referral-progress-label">{referralCount} / 3 paying referrals</div>
            <ReferralLinkBox />
          </>
        )}
        {referralCount >= 3 && (
          <>
            <div className="referral-card-title">🎉 Starter earned — you're in!</div>
            <div className="referral-card-body">
              Your three referrals came through. Starter is yours, free. Keep referring to climb the tier ladder.
            </div>
            <ReferralLinkBox />
          </>
        )}
      </div>
    );
  }

  // ── PAYING subscriber card ──
  const milestone1Done = referralCount >= 1;
  const milestone2Done = referralCount >= 4;
  const tierColor = TIER_COLORS[currentTier] || '#d4af37';

  return (
    <div className="referral-card paying">
      <div className="referral-card-header">
        <div className="referral-card-title">🤝 Refer colleagues, earn tier upgrades</div>
        <div className="referral-tier-badge" style={{ borderColor: tierColor, color: tierColor }}>
          {TIER_LABELS[currentTier] || currentTier}
        </div>
      </div>

      <div className="referral-milestones">
        <div className={`referral-milestone ${milestone1Done ? 'done' : 'pending'}`}>
          <div className="milestone-check">{milestone1Done ? '✅' : '○'}</div>
          <div className="milestone-body">
            <div className="milestone-title">Milestone 1 — 1 paying referral</div>
            <div className="milestone-desc">
              {milestone1Done
                ? 'Unlocked — your tier is boosted for 12 months.'
                : `+1 tier above ${TIER_LABELS[baseTier]} for 12 months, base price unchanged.`}
            </div>
          </div>
        </div>

        <div className={`referral-milestone ${milestone2Done ? 'done' : 'pending'}`}>
          <div className="milestone-check">{milestone2Done ? '✅' : '○'}</div>
          <div className="milestone-body">
            <div className="milestone-title">Milestone 2 — 4 paying referrals total</div>
            <div className="milestone-desc">
              {milestone2Done
                ? 'Unlocked — +2 tiers for 12 months.'
                : milestone1Done
                  ? `${4 - referralCount} more to go — +2 tiers above ${TIER_LABELS[baseTier]} for 12 months.`
                  : '+2 tiers above your base plan for 12 months.'}
            </div>
            {milestone1Done && !milestone2Done && (
              <div className="referral-progress-bar" style={{ marginTop: 8 }}>
                <div className="referral-progress-fill"
                  style={{ width: `${Math.round(((referralCount - 1) / 3) * 100)}%` }} />
              </div>
            )}
          </div>
        </div>
      </div>

      {milestone2Done && sub?.access_tier_expires_at && (
        <div className="referral-expiry">
          ⏳ Tier boost active until {new Date(sub.access_tier_expires_at.toDate?.() || sub.access_tier_expires_at).toLocaleDateString()}
          — refer 3 more before expiry to lock another year.
        </div>
      )}

      <ReferralLinkBox />
    </div>
  );
}

export default ReferralCard;